import React, { useState } from "react";
import { Box, BoxProps } from "../../";
import { styled } from "../../styled";
import { useIsomorphicLayoutEffect } from "../../hooks";

export type CollapsibleProps = {
  children?: React.ReactNode;
  /** Whether to show contents or not */
  show?: boolean;
  /** Duration of transition in ms */
  transitionDuration?: number;
} & Omit<BoxProps, "ref">;

export const Collapsible = ({
  children,
  show,
  transitionDuration = 200,
  ...boxProps
}: CollapsibleProps) => {
  const [innerContainer, setInnerContainer] = useState<HTMLDivElement | null>(
    null
  );

  // Whether the content of the collapsible should be hidden
  const [hidden, setHidden] = useState(!show);

  // max height of content container for transition purposes
  const [maxHeight, setMaxHeight] = useState(show ? undefined : 0);

  // Layout effect will run when new DOM tree is ready but not yet painted
  useIsomorphicLayoutEffect(() => {
    // Updates are not necessary if the innerContainer is not ready yet
    if (!innerContainer) {
      return;
    }

    const containerScrollHeight = Math.round(innerContainer?.scrollHeight ?? 0);
    const newMaxHeight = show ? containerScrollHeight : 0;
    if (newMaxHeight !== maxHeight) {
      setHidden(false);
      setMaxHeight(show ? newMaxHeight : 0);
    }
  }, [show, innerContainer, maxHeight]);

  // If show and hidden are not consistent it is because we are currently in a transition
  const transitioning = !show !== hidden;

  return (
    <Container
      onTransitionEnd={() => setHidden(!show)}
      transitionDuration={transitionDuration}
      maxHeight={maxHeight ?? `${maxHeight}px`}
      opacity={show ? 1 : 0}
      overflow="hidden"
      {...boxProps}
    >
      <Box
        visibility={!transitioning && hidden ? "hidden" : "visible"}
        // Using state instead of ref so we can be notified when the container is ready
        ref={(node) => setInnerContainer(node)}
      >
        {children}
      </Box>
    </Container>
  );
};

Collapsible.displayName = "Collapsible";

export default Collapsible;

type ContainerProps = {
  transitionDuration: number;
};

const Container = styled(Box)<ContainerProps>`
  transition: max-height ${(props) => props.transitionDuration}ms ease-out,
    opacity ${(props) => props.transitionDuration}ms ease-out;
`;
